<script setup lang="ts">
import { ref, computed, useSlots } from 'vue';
import ItpIcon from '../icon/ItpIcon.vue';

const props = withDefaults(
  defineProps<{
    id?: string;
    tag?: string;
    text?: string | number;
    defaultText?: string;
    spacing?: string | null;
    strikethrough?: boolean;
    strong?: boolean;
    small?: boolean;
    pre?: boolean;
    muted?: boolean;
    uppercase?: boolean | null;
    lowercase?: boolean | null;
    capitalize?: boolean | null;
    block?: boolean;
    noWrap?: boolean;
    wrap?: boolean | null;
    tooltip?: string | null;
    collapsible?: boolean;
    collapsibleDefaultState?: 'collapsed' | 'expanded';
    collapsibleCollapsedCharsLength?: number | null;
  }>(),
  {
    spacing: null,
    strikethrough: false,
    strong: false,
    small: false,
    pre: false,
    muted: false,
    uppercase: null,
    lowercase: null,
    capitalize: null,
    block: false,
    noWrap: true,
    wrap: null,
    tooltip: null,
    collapsible: false,
    collapsibleDefaultState: 'collapsed',
    collapsibleCollapsedCharsLength: null,
  },
);

const slots = useSlots();
const isCollapsed = ref(props.collapsibleDefaultState === 'collapsed');

const componentName = computed(() => {
  if (props.tag) {
    return props.tag;
  }
  if (props.strong) {
    return 'strong';
  }
  if (props.small) {
    return 'small';
  }
  if (props.strikethrough) {
    return 's';
  }
  return 'span';
});

const className = computed(() => {
  const result: Record<string, boolean> = {
    'text-muted': props.muted,
    'd-block': props.block,
    'text-nowrap': props.noWrap && !props.pre && !props.wrap,
    'text-wrap': !!props.wrap,
    'text-lowercase': !!props.lowercase,
    'text-uppercase': !!props.uppercase,
    'text-capitalize': !!props.capitalize,
    'overflow-hidden': !props.wrap && !props.pre,
    'text-truncate': !props.wrap && !props.pre,
  };

  if (props.text && props.spacing) {
    result[props.spacing] = true;
  }

  if (!props.text && props.spacing && slots.default) {
    result[props.spacing] = true;
  }

  return result;
});

const style = computed(() => {
  if (props.pre) {
    return 'white-space: pre-line';
  }
  return undefined;
});

const displayText = computed(() => {
  if (props.text == null || props.text === '') {
    return props.defaultText || '';
  }

  const textStr = String(props.text);

  if (props.collapsible && isCollapsed.value) {
    let result = textStr.split('\n')[0];
    if (
      props.collapsibleCollapsedCharsLength &&
      result.length > props.collapsibleCollapsedCharsLength
    ) {
      result = result.slice(0, props.collapsibleCollapsedCharsLength);
    }
    return `${result}...`;
  }

  return textStr;
});

const displayTooltip = computed(() => {
  if (!props.tooltip && props.collapsible && isCollapsed.value) {
    if (props.text == null || props.text === '') {
      return props.defaultText || '';
    }
    return props.text;
  }
  return props.tooltip;
});

const displayToggle = computed(() => props.collapsible);

const toggleIcon = computed(() =>
  isCollapsed.value ? 'mdi:caret-down' : 'mdi:caret-up',
);

function toggle() {
  if (props.collapsible) {
    isCollapsed.value = !isCollapsed.value;
  }
}
</script>

<template>
  <component
    :is="componentName"
    :class="className"
    :style="style"
    :title="displayTooltip"
  >
    <slot name="before" />
    <slot>{{ displayText }}</slot>
    <slot name="after" />
    <ItpIcon
      v-if="displayToggle"
      :name="toggleIcon"
      clickable
      @click="toggle"
    />
  </component>
</template>

